import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import { ActualSelected, Utils } from '../../../../../shared/utils';
import { EfectivoService } from '../../../../../services/finanzas/efectivo/efectivo.service';
import { Efectivo, IngresoACaja, MovimientoSocio } from '../../../../../models/finanzas/efectivo/efectivo.model';
import { EfectivoDialogIngresoACajaComponent } from '../../../../../modules/finanzas/efectivo/efectivo-dialog-ingreso-a-caja.component';
import { EfectivoDialogMovimientoSocioComponent } from '../../../../../modules/finanzas/efectivo/efectivo-dialog-movimiento-socio.component';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';

export type CashMovementKind = 'bank' | 'partnerWithdraw' | 'partnerDeposit';

export interface CashMovementLine {
  uid: string;
  date: any;
  concept: string;
  meta: string;
  amount: number;
}

export interface CashMovementBlock {
  kind: CashMovementKind;
  title: string;
  singular: string;
  icon: string;
  total: number;
  count: number;
  lines: CashMovementLine[];
}

const MAX_LINES = 5;

@Component({
  selector: 'app-cash-detail-panel',
  templateUrl: './cash-detail-panel.component.html',
  styleUrls: ['./cash-detail-panel.component.css'],
  standalone: false,
})
export class CashDetailPanelComponent implements OnChanges {
  @Input() row!: Efectivo;
  @Output() changed = new EventEmitter<void>();
  @Output() ledger = new EventEmitter<void>();

  readonly year = ActualSelected.yearSelected;
  readonly maxLines = MAX_LINES;

  loading = true;
  error = false;
  blocks: CashMovementBlock[] = [];

  private loadedSignature = '';

  constructor(
    private efectivoService: EfectivoService,
    private dialog: MatDialog,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get movementsCount(): number {
    return this.blocks.reduce((acc, block) => acc + block.count, 0);
  }

  ngOnChanges(): void {
    if (!this.row || !this.row.uid) return;
    const signature = [this.row.uid, this.row.saldoContable, this.row.fondoDeCaja].join('|');
    if (signature === this.loadedSignature) return;
    this.loadedSignature = signature;
    this.load(this.row.uid);
  }

  retry(): void {
    if (!this.row) return;
    this.load(this.row.uid);
  }

  add(block: CashMovementBlock): void {
    this.openDialog(block);
  }

  manage(block: CashMovementBlock): void {
    this.openDialog(block);
  }

  editLine(block: CashMovementBlock, line: CashMovementLine): void {
    this.openDialog(block, line.uid);
  }

  deleteLine(block: CashMovementBlock, line: CashMovementLine): void {
    const action = block.kind === 'bank'
      ? () => this.efectivoService.ingresoACajaDelete(line.uid)
      : () => this.efectivoService.movimientoSocioDelete(line.uid);

    this.confirm.open({
      title: `Eliminar ${block.singular}`,
      subtitle: this.row.nombre || undefined,
      message: block.kind === 'bank'
        ? 'Se eliminará el ingreso y su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente. Esta acción no se puede deshacer.'
        : 'Se eliminará el movimiento del socio y su asiento contable. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.details(line),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando movimiento…',
      errorText: 'Ha ocurrido un error al eliminar el movimiento',
      action,
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Movimiento eliminado correctamente');
      this.refresh();
    });
  }

  trackByKind(_: number, block: CashMovementBlock): string {
    return block.kind;
  }

  trackByUid(_: number, line: CashMovementLine): string {
    return line.uid;
  }

  private openDialog(block: CashMovementBlock, editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    const dialog$ = block.kind === 'bank'
      ? EfectivoDialogIngresoACajaComponent.open(this.dialog, { efectivo: this.row, editUid })
      : EfectivoDialogMovimientoSocioComponent.open(this.dialog, { efectivo: this.row, tipoMovimientoSocioId: block.kind === 'partnerWithdraw' ? 1 : 2, editUid });
    dialog$.subscribe(changed => { if (changed) this.refresh(); });
  }

  private refresh(): void {
    this.changed.emit();
  }

  private details(line: CashMovementLine): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (line.date) details.push({ label: 'Fecha', value: formatDate(line.date, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Concepto', value: line.concept || 'Sin concepto' });
    if (line.meta) details.push({ label: 'Detalle', value: line.meta });
    details.push({ label: 'Importe', value: Utils.FormatMoney(line.amount) + ' €' });
    return details;
  }

  private load(uid: string): void {
    this.loading = this.blocks.length === 0;
    this.error = false;

    Promise.all([
      this.efectivoService.ingresoACajaList(uid),
      this.efectivoService.movimientoSocioList(uid, 1),
      this.efectivoService.movimientoSocioList(uid, 2),
    ])
      .then(([bank, withdraws, deposits]) => {
        if (!this.row || uid !== this.row.uid) return;
        this.blocks = [
          this.buildBankBlock(Array.isArray(bank) ? bank : []),
          this.buildPartnerBlock('partnerWithdraw', 'Reintegros del socio', 'reintegro del socio', 'remove_circle_outline', Array.isArray(withdraws) ? withdraws : []),
          this.buildPartnerBlock('partnerDeposit', 'Ingresos del socio', 'ingreso del socio', 'person_add', Array.isArray(deposits) ? deposits : []),
        ];
      })
      .catch(() => { this.error = true; })
      .then(() => { this.loading = false; });
  }

  private buildBankBlock(items: IngresoACaja[]): CashMovementBlock {
    return {
      kind: 'bank',
      title: 'Ingresos desde banco',
      singular: 'ingreso desde banco',
      icon: 'move_to_inbox',
      total: this.sum(items),
      count: items.length,
      lines: items.slice(0, MAX_LINES).map(item => ({
        uid: item.uid,
        date: item.fecha,
        concept: item.concepto,
        meta: [item.bankAccountInfo, item.comisiones ? 'Comisiones ' + Utils.FormatMoney(item.comisiones) + ' €' : ''].filter(Boolean).join(' · '),
        amount: Number(item.importe) || 0,
      })),
    };
  }

  private buildPartnerBlock(kind: CashMovementKind, title: string, singular: string, icon: string, items: MovimientoSocio[]): CashMovementBlock {
    return {
      kind,
      title,
      singular,
      icon,
      total: this.sum(items),
      count: items.length,
      lines: items.slice(0, MAX_LINES).map(item => ({
        uid: item.uid,
        date: item.fecha,
        concept: item.concepto,
        meta: [item.partnersName, item.comisiones ? 'Comisiones ' + Utils.FormatMoney(item.comisiones) + ' €' : ''].filter(Boolean).join(' · '),
        amount: Number(item.importe) || 0,
      })),
    };
  }

  private sum(items: Array<{ importe: number }>): number {
    return Utils.RoundTwoDecimals(items.reduce((acc, item) => acc + (Number(item.importe) || 0), 0));
  }
}
